import { getOrgServerBaseUrl, getOrgId } from "lib/config";

export type OrgARC = {
    serialNumber: string;
    name: string;
    /** Versions the cacheable thumbnail URL; null when none was uploaded. */
    thumbnailHash: string | null;
    tailscaleIp: string | null;
};

export type OrgProxy = {
    publicHostname: string;
} | null;

export type OrgMissionAction = {
    sourceArcSerial: string;
    sourceActionId: string;
    sourceMissionId: string;
    sequenceOrder: number;
    actionType: string;
    name: string;
    note: string;
    args: Record<string, unknown>;
    createdAt: string;
};

export type OrgMission = {
    sourceArcSerial: string;
    sourceMissionId: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    recordVideo: boolean;
    importedAt: string;
    actionCount: number;
};

export type OrgMissionDetails = OrgMission & {
    actions: OrgMissionAction[];
};

export type OrgMissionOverview = {
    totalMissions: number;
    totalRuns: number;
};

export type OrgMissionRun = {
    sourceArcSerial: string;
    sourceRunId: string;
    sourceMissionId: string;
    missionName: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    logCount: number;
};

const ORG_SERVER_URL = getOrgServerBaseUrl();

const fetchGql = (token: string | null, query: string, variables: Record<string, unknown>) =>
    fetch(`${ORG_SERVER_URL}/graphql`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, variables }),
    });

export const refresh = async (): Promise<void> => {
    const refreshToken = localStorage.getItem("orgRefreshToken");
    if (!refreshToken) throw new Error("401 No refresh token");
    const res = await fetch(`${ORG_SERVER_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) throw new Error("401 Refresh failed");
    const { userToken, refreshToken: newRefresh } = await res.json();
    localStorage.setItem("orgUserToken", userToken);
    localStorage.setItem("orgRefreshToken", newRefresh);
};

const gql = async <T>(query: string, variables: Record<string, unknown> = {}): Promise<T> => {
    let res = await fetchGql(localStorage.getItem("orgUserToken"), query, variables);

    if (res.status === 401) {
        await refresh(); // throws "401 ..." if refresh fails — propagates to caller
        res = await fetchGql(localStorage.getItem("orgUserToken"), query, variables);
    }

    if (res.status === 401) throw new Error("401 Unauthorized");

    const { data, errors } = await res.json();
    if (errors?.length) throw new Error(errors[0].message);
    return data as T;
};

export const getArcs = (): Promise<OrgARC[]> =>
    gql<{ arcs: OrgARC[] }>(`query { arcs { serialNumber name thumbnailHash tailscaleIp } }`).then((d) => d.arcs);

export const renameArc = (serialNumber: string, name: string): Promise<OrgARC> =>
    gql<{ renameArc: OrgARC }>(
        `mutation RenameArc($serialNumber: String!, $name: String!) {
            renameArc(serialNumber: $serialNumber, name: $name) { serialNumber name thumbnailHash tailscaleIp }
        }`,
        { serialNumber, name }
    ).then((d) => d.renameArc);

/** Upload a PNG data URL (org_server stores it as WebP), or null to clear. Returns the new hash. */
export const setArcThumbnail = (serialNumber: string, thumbnail: string | null): Promise<string | null> =>
    gql<{ setArcThumbnail: { thumbnailHash: string | null } }>(
        `mutation SetArcThumbnail($serialNumber: String!, $thumbnail: String) {
            setArcThumbnail(serialNumber: $serialNumber, thumbnail: $thumbnail) { thumbnailHash }
        }`,
        { serialNumber, thumbnail }
    ).then((d) => d.setArcThumbnail.thumbnailHash);

/**
 * Fetch a thumbnail image. The URL is versioned by hash and served with
 * `Cache-Control: private, immutable`, so the browser's HTTP cache answers
 * repeat requests even though they carry the bearer token.
 */
export const fetchArcThumbnail = async (serialNumber: string, thumbnailHash: string): Promise<Blob> => {
    const url = `${ORG_SERVER_URL}/arcs/${encodeURIComponent(serialNumber)}/thumbnail/${thumbnailHash}`;
    const get = () => {
        const token = localStorage.getItem("orgUserToken");
        return fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    };
    let res = await get();
    if (res.status === 401) {
        await refresh(); // throws "401 ..." if refresh fails
        res = await get();
    }
    if (!res.ok) throw new Error(`Thumbnail fetch failed: ${res.status}`);
    return res.blob();
};

export const getProxy = (): Promise<OrgProxy> =>
    gql<{ proxy: OrgProxy }>(`query { proxy { publicHostname } }`).then((d) => d.proxy);

export const getMissions = (limit = 500, offset = 0): Promise<OrgMission[]> =>
    gql<{ missions: OrgMission[] }>(
        `query OrgMissions($limit: Int!, $offset: Int!) {
            missions(limit: $limit, offset: $offset) {
                sourceArcSerial sourceMissionId name createdAt updatedAt
                recordVideo importedAt actionCount
            }
        }`,
        { limit, offset }
    ).then((d) => d.missions);

export const getMission = (
    sourceArcSerial: string,
    sourceMissionId: string
): Promise<OrgMissionDetails | null> =>
    gql<{ mission: OrgMissionDetails | null }>(
        `query OrgMission($sourceArcSerial: String!, $sourceMissionId: String!) {
            mission(sourceArcSerial: $sourceArcSerial, sourceMissionId: $sourceMissionId) {
                sourceArcSerial sourceMissionId name createdAt updatedAt
                recordVideo importedAt actionCount
                actions {
                    sourceArcSerial sourceActionId sourceMissionId sequenceOrder
                    actionType name note args createdAt
                }
            }
        }`,
        { sourceArcSerial, sourceMissionId }
    ).then((d) => d.mission);

export const getMissionOverview = (): Promise<OrgMissionOverview> =>
    gql<{ missionOverview: OrgMissionOverview }>(
        `query OrgMissionOverview {
            missionOverview { totalMissions totalRuns }
        }`
    ).then((d) => d.missionOverview);

export const getRuns = (limit = 1000, offset = 0): Promise<OrgMissionRun[]> =>
    gql<{ runs: OrgMissionRun[] }>(
        `query OrgMissionRuns($limit: Int!, $offset: Int!) {
            runs(limit: $limit, offset: $offset) {
                sourceArcSerial sourceRunId sourceMissionId missionName
                status createdAt updatedAt logCount
            }
        }`,
        { limit, offset }
    ).then((d) => d.runs);

export const login = async (username: string, password: string): Promise<void> => {
    // The "organization id" typed by the user is the org's slug (Organization.slug);
    // org_server keys on `slug` (body field or X-Fleet-Slug header).
    const slug = getOrgId();
    const res = await fetch(`${ORG_SERVER_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, username, password }),
    });
    if (!res.ok) throw new Error("Login failed");
    const { userToken, refreshToken } = await res.json();
    localStorage.setItem("orgUserToken", userToken);
    localStorage.setItem("orgRefreshToken", refreshToken);
};

export const logout = (): void => {
    localStorage.removeItem("orgUserToken");
    localStorage.removeItem("orgRefreshToken");
};
